'use client';

import React, { useState } from 'react';
import { Search, X, Clock, ChevronRight } from 'lucide-react';
import { Service } from './types';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  services: Service[];
  onSelectService: (service: Service) => void;
}

export const SearchModal: React.FC<SearchModalProps> = ({
  isOpen,
  onClose,
  services,
  onSelectService,
}) => {
  const [query, setQuery] = useState('');

  if (!isOpen) return null;

  const filtered = services.filter((s) => {
    const q = query.toLowerCase().trim();
    if (!q) return true;
    return (
      s.name.toLowerCase().includes(q) ||
      (s.description && s.description.toLowerCase().includes(q)) ||
      (s.category && s.category.toLowerCase().includes(q))
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-start justify-center p-4 pt-16 sm:pt-24">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 flex flex-col gap-4 animate-fade-in">
        {/* Input de Búsqueda */}
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
          <div className="flex items-center gap-2.5 flex-1">
            <Search className="w-5 h-5 text-slate-400 shrink-0" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar servicio (ej. Corte, Lavado, Barba)..."
              className="w-full text-sm font-semibold text-[#0f172a] placeholder-slate-400 bg-transparent focus:outline-none"
            />
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Resultados */}
        <div className="flex flex-col gap-2.5 max-h-80 overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-xs">
              No se encontraron servicios que coincidan con &quot;{query}&quot;.
            </div>
          ) : (
            filtered.map((service) => (
              <div
                key={service.id}
                onClick={() => {
                  onSelectService(service);
                  onClose();
                }}
                className="group p-3 rounded-2xl hover:bg-slate-50 border border-transparent hover:border-slate-200 flex items-center justify-between gap-3 transition cursor-pointer"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-slate-100 shrink-0">
                    <img
                      src={
                        service.imageUrl ||
                        'https://images.unsplash.com/photo-1599351431202-1e0f0137899a?w=150&h=150&fit=crop'
                      }
                      alt={service.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-xs sm:text-sm font-bold text-[#0f172a] truncate group-hover:text-emerald-700">
                      {service.name}
                    </h4>
                    <p className="text-[11px] text-slate-400 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {service.duration} min · {service.category}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-extrabold text-xs sm:text-sm text-[#0f172a] whitespace-nowrap">
                    ${' '}
                    {service.cashPrice.toLocaleString('es-AR', {
                      minimumFractionDigits: 0,
                    })}
                  </span>
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#0f172a] transition" />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
